import { useCallback, useState } from "react";
import { apiJson } from "@/api/client";
import { withQuery } from "@/api/query";

/**
 * Mirrors api/src/pbo_invoicing/fx/routes.py: units of `to` per one `from` on `on`, from the
 * free currency API. Only ever a suggestion - it fills a field the director then confirms.
 */
export type FxQuote = {
  from: string;
  to: string;
  on: string;
  rate: string;
  source: string;
  published_on: string;
};

export function fetchQuote(from: string, to: string, on: string): Promise<FxQuote> {
  return apiJson<FxQuote>(withQuery("/fx/quote", { from, to, on }));
}

/** "currency-api.pages.dev, published 2026-10-20": what a saved rate says it came from. */
export function quoteSource(quote: FxQuote): string {
  return `${quote.source}, published ${quote.published_on}`;
}

/** "83.952100" as a person writes it: "83.9521". */
export function plainRate(rate: string): string {
  return String(Number(rate));
}

export const CURRENCY_CODE = /^[A-Z]{3}$/;

/** Fetch one or more quotes for a day at once; `error` holds why the last attempt failed. */
export function useQuoteLookup() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const lookup = useCallback(
    async (on: string, ...pairs: [string, string][]): Promise<FxQuote[] | null> => {
      setBusy(true);
      setError(null);
      try {
        return await Promise.all(pairs.map(([from, to]) => fetchQuote(from, to, on)));
      } catch (err) {
        setError(err);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [],
  );
  return { busy, error, lookup };
}
